import { addDays, isBefore, isPast } from 'date-fns';
import { Suspense, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { FiatWalletAccountState } from '#src/__generated__/globalTypes.ts';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { CreateFiatWalletSteps } from '#src/components/fiatWallet/CreateFiatWallet/type.ts';
import {
  PRIVACY_POLICY,
  TERMS_AND_CONDITIONS,
} from '#src/constants/__generated__/routes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useDeeplinkFromNativeApp } from '#src/hooks/useDeeplinkFromNativeApp.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { LIFECYCLE, useLifecycle } from '#src/hooks/useLifecycle.ts';
import { useQueryString } from '#src/hooks/useQueryString.ts';
import { useCashWalletKycEvents } from '#src/hooks/wallets/useCashWalletKycEvents.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { lazy } from '#src/lib/retry.ts';
import {
  EnterCashWalletKYCFlow_Source,
  EnterCashWalletKYCFlow_Target,
} from '#src/protos/events/platform/web/events.ts';

const CreateFiatWallet = lazy(
  async () => import('#src/components/fiatWallet/CreateFiatWallet/index.tsx'),
  'CreateFiatWallet'
);

const cashWalletLaunchDate = new Date('2023-08-01T00:00:00.000Z');

export const WalletSetup = () => {
  const { trackEnter } = useCashWalletKycEvents();
  const {
    currentUser,
    walletPreferences: { hasMigratedAndSetupWallets },
  } = useCurrentUserContext();
  const { hasDeeplink } = useDeeplinkFromNativeApp();

  const location = useLocation();
  const navigate = useNavigate();
  const { update, loading, lifecycle } = useLifecycle();

  const [shouldSetupWalletNow, setShouldSetupWalletNow] =
    useState<boolean>(false);

  const { fiatBalanceStatus } = useFiatBalance();
  const action = useQueryString('action');
  const needsToKycBecauseRefused = action === 'kyc';

  const { isMobileApp } = useIsMobileApp();

  if (!currentUser) {
    return null;
  }

  const userCreatedAt = currentUser.createdAt;

  const userHasBeenCreatedBeforeCashWalletLaunch = isBefore(
    userCreatedAt,
    cashWalletLaunchDate
  );

  const userHasSetupWallet = !!lifecycle?.userHasSetupWallet;

  const nextTimeUserShouldSetupWallet =
    lifecycle?.nextTimeUserShouldSetupWallet;

  const onClose = () => {
    if (!userHasSetupWallet) {
      update(LIFECYCLE.userHasSetupWallet, true);
    }

    if (
      !nextTimeUserShouldSetupWallet ||
      isPast(nextTimeUserShouldSetupWallet)
    ) {
      update(
        LIFECYCLE.nextTimeUserShouldSetupWallet,
        addDays(new Date(), 7).toISOString()
      );
    }

    setShouldSetupWalletNow(false);
    if (needsToKycBecauseRefused) {
      // remove the ?action=
      navigate(location.pathname);
    }
  };

  if (
    !hasDeeplink &&
    !userHasSetupWallet &&
    !loading &&
    (!hasMigratedAndSetupWallets || userHasBeenCreatedBeforeCashWalletLaunch) &&
    !shouldSetupWalletNow
  ) {
    setShouldSetupWalletNow(true);
    trackEnter({
      source: EnterCashWalletKYCFlow_Source.MIGRATION_MODAL,
      target: EnterCashWalletKYCFlow_Target.OPTIONAL_VALIDATED_OWNER,
    });
  }

  if (!loading && !hasMigratedAndSetupWallets && !shouldSetupWalletNow) {
    if (!nextTimeUserShouldSetupWallet) {
      update(
        LIFECYCLE.nextTimeUserShouldSetupWallet,
        addDays(new Date(), 2).toISOString()
      );
    } else if (isPast(nextTimeUserShouldSetupWallet) && !hasDeeplink) {
      setShouldSetupWalletNow(true);
      trackEnter({
        source: EnterCashWalletKYCFlow_Source.MIGRATION_MODAL,
        target: EnterCashWalletKYCFlow_Target.OPTIONAL_VALIDATED_OWNER,
      });
    }
  }

  if (
    !hasDeeplink &&
    needsToKycBecauseRefused &&
    fiatBalanceStatus !== FiatWalletAccountState.VALIDATED_OWNER &&
    !shouldSetupWalletNow
  ) {
    setShouldSetupWalletNow(true);
    trackEnter({
      source: EnterCashWalletKYCFlow_Source.REJECTED_KYC_EMAIL,
      target: EnterCashWalletKYCFlow_Target.OPTIONAL_VALIDATED_OWNER,
    });
  }

  const isAuthorizedRoute =
    !location.pathname.match(TERMS_AND_CONDITIONS) ||
    !location.pathname.match(PRIVACY_POLICY);

  if (!shouldSetupWalletNow || !isAuthorizedRoute || isMobileApp) {
    return null;
  }

  const initialStep = () => {
    if (needsToKycBecauseRefused) return undefined;
    if (userHasSetupWallet)
      return CreateFiatWalletSteps.ACTIVATE_YOUR_CASH_WALLET;
    return CreateFiatWalletSteps.WHATS_NEW;
  };
  return (
    <Suspense fallback={<LoadingIndicator />}>
      <CreateFiatWallet
        initialStep={initialStep()}
        statusTarget={FiatWalletAccountState.VALIDATED_OWNER}
        canDismissAfterActivation={!needsToKycBecauseRefused}
        onClose={onClose}
        onDismissActivationSuccess={onClose}
        onDismissBeforeActivation={onClose}
      />
    </Suspense>
  );
};
